import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  User, 
  UserCheck, 
  UserX, 
  Clock,
  Users,
  Mail,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDate } from '@/lib/utils';

interface AttendanceRecord {
  class_id: string;
  participant_id: string;
  first_name: string;
  last_name: string;
  email: string;
  session_id: string;
  was_present: boolean;
  status_presenca: string;
  check_in_time: string | null;
}

interface AttendanceModalProps {
  sessionId: string;
  sessionData?: {
    id: string;
    start_date: string;
    duration_minutes: number;
    classes?: {
      courses?: {
        name: string;
      };
    };
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AttendanceModal = ({ 
  sessionId, 
  sessionData, 
  open, 
  onOpenChange 
}: AttendanceModalProps) => {
  const [loading, setLoading] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [registeredParticipants, setRegisteredParticipants] = useState<AttendanceRecord[]>([]);

  useEffect(() => {
    if (open && sessionId) {
      fetchAttendanceData();
    }
  }, [open, sessionId]);

  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      // Buscar dados da view attendance_report
      const { data, error } = await supabase
        .from('attendance_report')
        .select('*')
        .eq('session_id', sessionId);

      if (error) throw error;

      setAttendanceRecords(data || []);

      // Buscar participantes inscritos (mesmo que não tenham presença registrada)
      const { data: sessionInfo, error: sessionError } = await supabase
        .from('class_sessions')
        .select('class_id')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      const { data: participants, error: participantsError } = await supabase
        .from('class_participants')
        .select('id, first_name, last_name, email')
        .eq('class_id', sessionInfo.class_id)
        .eq('status', 'registered');

      if (participantsError) throw participantsError;

      // Criar lista consolidada
      const consolidated = participants?.map(participant => {
        const attendanceRecord = data?.find(record => record.participant_id === participant.id);
        return {
          class_id: sessionInfo.class_id,
          participant_id: participant.id,
          first_name: participant.first_name,
          last_name: participant.last_name,
          email: participant.email,
          session_id: sessionId,
          was_present: attendanceRecord?.was_present || false,
          status_presenca: attendanceRecord?.status_presenca || 'ausente',
          check_in_time: attendanceRecord?.check_in_time || null
        };
      }) || [];

      setRegisteredParticipants(consolidated);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      toast.error('Erro ao carregar dados de presença');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string, wasPresent: boolean) => {
    if (wasPresent) {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          Presente
        </Badge>
      );
    } else {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          <XCircle className="h-3 w-3 mr-1" />
          Ausente
        </Badge>
      );
    }
  };

  const presentCount = registeredParticipants.filter(p => p.was_present).length;
  const totalCount = registeredParticipants.length;
  const attendanceRate = totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Lista de Presença
          </DialogTitle>
          <DialogDescription>
            {sessionData && (
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4" />
                  {new Date(sessionData.start_date).toLocaleDateString('pt-BR')} às {new Date(sessionData.start_date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4" />
                  {sessionData.duration_minutes} minutos
                </div>
                {sessionData.classes?.courses?.name && (
                  <div className="text-sm font-extrabold">
                    {sessionData.classes.courses.name}
                  </div>
                )}
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Estatísticas de Presença */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Total Inscritos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold">{totalCount}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Presentes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold text-green-600">{presentCount}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Taxa de Presença</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold">{attendanceRate}%</div>
              </CardContent>
            </Card>
          </div>

          {/* Lista de Participantes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Participantes</CardTitle>
              <CardDescription>
                Relação de todos os participantes inscritos e sua presença na sessão
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : registeredParticipants.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <User className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>Nenhum participante inscrito</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Participante</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Check-in</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {registeredParticipants.map((participant) => (
                      <TableRow key={participant.participant_id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {participant.was_present ? (
                              <UserCheck className="h-4 w-4 text-green-600" />
                            ) : (
                              <UserX className="h-4 w-4 text-red-500" />
                            )}
                            <span className="font-extrabold">
                              {participant.first_name} {participant.last_name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{participant.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(participant.status_presenca, participant.was_present)}
                        </TableCell>
                        <TableCell>
                          {participant.check_in_time ? (
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              {new Date(participant.check_in_time).toLocaleTimeString('pt-BR', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AttendanceModal;
